import logger from '~/services/logger';
import type { Feed, FeedEntry } from '~/services/layout/index.ts';
import { isValidIsoCalendarDate } from '~/services/plugins/quick-brick-component-text-field/dateMask.ts';
import {
  FormComponentTypes,
  type BaseFormField,
  type FormButtonField,
  type FormCheckBoxField,
  type FormConfig,
  type FormField,
  type FormLabelField,
  type FormMultiSelectField,
  type FormSingleSelectField,
  type FormUiComponent,
  type FormValueValidator,
  type LayoutPresetsInput,
  type TextInputType,
  type TextLikeFormField,
  type UIComponentsDataSources,
  isDateInputType,
  isNumberInputType,
  isTextInputField,
  isTextLikeField,
  resolveTextInputType,
} from './types.ts';
import {
  DEFAULT_FORM_VALIDATION_MESSAGES,
  interpolateTemplate,
  type FormValidationMessages,
} from './form-validation-messages.ts';

function resolveInputType(field: FormField): TextInputType {
  return resolveTextInputType({
    formType: field.type,
    inputType: isTextInputField(field) ? field.options?.inputType : undefined,
  });
}

type ActionEntryInput = {
  id?: string;
  title?: string;
  button: BaseFormField;
  type: (typeof FormComponentTypes)[keyof typeof FormComponentTypes];
  tag?: string;
  extraExtensions?: Record<string, unknown>;
};

export type ComponentDataResult = {
  uiComponents: FormUiComponent[];
  uiComponentsDataSource: UIComponentsDataSources;
};

export type LoadRemoteFeedFn = (url: string) => Promise<Feed>;

type PrepareComponentInput = {
  formData: FormField;
  qbComponent: FormUiComponent;
  formId: string;
  index: number;
};

/**
 * Assigns unique ids to a component tree rooted at the given id prefix.
 *
 * @param component - UI component subtree to update in place.
 * @param componentId - Id assigned to the root component.
 */
function assignNestedComponentIds(
  component: FormUiComponent,
  componentId: string
): void {
  component.id = componentId;

  component.ui_components?.forEach((child, childIndex) => {
    assignNestedComponentIds(child, `${componentId}-${childIndex}`);
  });
}

/**
 * Clones a layout preset and assigns unique component ids for a form field instance.
 *
 * @param input - Form field, preset template, form id, and field index.
 * @returns A unique UI component instance for the form field.
 */
function prepareComponent({
  formData,
  qbComponent,
  formId,
  index,
}: PrepareComponentInput): FormUiComponent {
  const uiFormId: string = formData.id || `form-${index}`;
  const component: FormUiComponent = structuredClone(qbComponent);

  assignNestedComponentIds(component, `${uiFormId}-${formId}-${index}`);

  if (isTextLikeField(formData)) {
    component.data = {
      ...(component.data ?? {}),
      field_id: formData.id,
      inputType: resolveInputType(formData),
    };
  }

  return component;
}

function createFeed(id: string, title: string): Feed {
  return {
    id,
    extensions: {},
    title,
    entry: [],
    type: { value: 'feed' },
  };
}

function createActionEntry({
  id = undefined,
  title = undefined,
  button,
  type,
  tag,
  extraExtensions = {},
}: ActionEntryInput): FeedEntry {
  const optionsExtensions =
    button.options &&
    'extensions' in button.options &&
    button.options.extensions
      ? (button.options.extensions as Record<string, unknown>)
      : button.extensions ?? {};

  return {
    id: id ?? button.id,
    extensions: {
      ...optionsExtensions,
      ...extraExtensions,
      formType: type,
      ...(tag ? { tag } : {}),
    },
    title: title ?? button.options?.title ?? button.title ?? button.id,
    type: { value: 'action' },
  };
}

function buildPreferenceEditorExtensions(
  fieldId: string,
  selectMode: 'single' | 'multi'
): Record<string, unknown> {
  return {
    preference_editor_options: {
      key: fieldId,
      scope: 'screen',
      select_mode: selectMode,
    },
    role: 'preference_editor',
  };
}

/**
 * Creates a synthetic data source feed for a single form field component.
 *
 * @param field - Form field definition.
 * @param component - UI component instance.
 * @param loadRemoteFeed - Optional remote feed loader for itemsFeedURL fields.
 * @returns Feed populated for the given field type.
 */
export async function createDataSource(
  field: FormField,
  component: FormUiComponent,
  loadRemoteFeed?: LoadRemoteFeedFn
): Promise<Feed> {
  const feed = createFeed(component.id, field.options?.title ?? field.id);

  switch (field.type) {
    case FormComponentTypes.CheckBox:
      return createCheckboxDataSource(feed, field);
    case FormComponentTypes.MultiSelect:
      return createMultiSelectDataSource(feed, field, loadRemoteFeed);
    case FormComponentTypes.SingleSelect:
      return createSingleSelectDataSource(feed, field, loadRemoteFeed);
    case FormComponentTypes.TextInput:
    case FormComponentTypes.DatePicker:
      return createTextInputDataSource(feed, field);
    case FormComponentTypes.Label:
      return createLabelDataSource(feed, field);
    case FormComponentTypes.Button:
      return createButtonDataSource(feed, field);
    default:
      throw new Error(
        `createDataSource: unknown form component type ${
          (field as FormField).type
        }`
      );
  }
}

function createLabelDataSource(feed: Feed, field: FormLabelField): Feed {
  feed.entry.push(
    createActionEntry({ button: field, type: FormComponentTypes.Label })
  );
  return feed;
}

function createButtonDataSource(feed: Feed, field: FormButtonField): Feed {
  feed.entry.push(createActionEntry({ button: field, type: field.type }));
  return feed;
}

function createTextInputDataSource(feed: Feed, field: TextLikeFormField): Feed {
  const inputType = resolveInputType(field);
  const lengthOptions =
    isDateInputType(inputType) || !isTextInputField(field)
      ? {}
      : {
          minLength: field.options?.minLength,
          maxLength: field.options?.maxLength,
        };

  feed.entry.push(
    createActionEntry({
      button: field,
      type: field.type,
      extraExtensions: {
        description: field.options?.description,
        required: field.options?.required ?? false,
        inputType,
        ...lengthOptions,
      },
    })
  );
  return feed;
}

async function createSingleSelectDataSource(
  feed: Feed,
  field: FormSingleSelectField,
  loadRemoteFeed?: LoadRemoteFeedFn
): Promise<Feed> {
  if (field.options.itemsFeedURL && loadRemoteFeed) {
    const loadedFeed = await loadRemoteFeed(field.options.itemsFeedURL);
    loadedFeed.extensions = {
      ...loadedFeed.extensions,
      ...buildPreferenceEditorExtensions(field.id, 'single'),
    };
    return loadedFeed;
  }

  Object.assign(
    feed.extensions ?? {},
    buildPreferenceEditorExtensions(field.id, 'single')
  );

  field.options.items.forEach((entry) => {
    feed.entry.push(
      createActionEntry({
        id: `${field.options.title}-${entry.const}`,
        title: entry.title,
        button: field,
        type: FormComponentTypes.SingleSelect,
        tag: entry.const,
      })
    );
  });

  return feed;
}

async function createMultiSelectDataSource(
  feed: Feed,
  field: FormMultiSelectField,
  loadRemoteFeed?: LoadRemoteFeedFn
): Promise<Feed> {
  if (field.options.itemsFeedURL && loadRemoteFeed) {
    const loadedFeed = await loadRemoteFeed(field.options.itemsFeedURL);
    loadedFeed.extensions = {
      ...loadedFeed.extensions,
      ...buildPreferenceEditorExtensions(field.id, 'multi'),
    };
    return loadedFeed;
  }

  Object.assign(
    feed.extensions ?? {},
    buildPreferenceEditorExtensions(field.id, 'multi')
  );

  field.options.items?.forEach((entry) => {
    feed.entry.push(
      createActionEntry({
        id: `${field.options.title}-${entry.const}`,
        title: entry.title,
        button: field,
        type: FormComponentTypes.CheckBox,
        tag: entry.const,
      })
    );
  });

  return feed;
}

function createCheckboxDataSource(feed: Feed, field: FormCheckBoxField): Feed {
  Object.assign(
    feed.extensions ?? {},
    buildPreferenceEditorExtensions(field.id, 'single')
  );

  feed.entry.push(
    createActionEntry({
      id: `${field.options?.title ?? field.id}-${field.id}-true`,
      title: 'YES',
      button: field,
      type: FormComponentTypes.SingleSelect,
      tag: 'true',
    }),
    createActionEntry({
      id: `${field.options?.title ?? field.id}-${field.id}-false`,
      title: 'NO',
      button: field,
      type: FormComponentTypes.SingleSelect,
      tag: 'false',
    })
  );

  return feed;
}

/**
 * Creates a validator function for a form field based on its options.
 *
 * @param component - Form field definition.
 * @param messages - Localized validation copy. Defaults to English.
 * @returns Validator returning error messages for invalid values.
 */
export function createValidator(
  component: FormField,
  messages: FormValidationMessages = DEFAULT_FORM_VALIDATION_MESSAGES
): FormValueValidator {
  const validators: FormValueValidator[] = [];
  const inputType = resolveInputType(component);
  const isDateMode = isDateInputType(inputType);

  if (component.options?.required) {
    validators.push((value) => (value?.trim() ? [] : [messages.required]));
  }

  if (isTextLikeField(component)) {
    validators.push((value) => {
      const trimmed = value?.trim() ?? '';
      if (!trimmed) return [];
      if (isDateInputType(inputType) && !isValidIsoCalendarDate(trimmed)) {
        return [messages.invalidDate];
      }
      if (isNumberInputType(inputType) && !Number.isFinite(Number(trimmed))) {
        return [messages.invalidNumber];
      }
      return [];
    });
  }

  if (isTextInputField(component) && component.options && !isDateMode) {
    if (component.options.minLength !== undefined) {
      const minLength = component.options.minLength;
      validators.push((value) =>
        (value?.length ?? 0) >= minLength
          ? []
          : [
              interpolateTemplate({
                template: messages.minLength,
                values: { minLength },
              }),
            ]
      );
    }

    if (component.options.maxLength !== undefined) {
      const maxLength = component.options.maxLength;
      validators.push((value) => {
        if (!value) return [];
        return value.length <= maxLength
          ? []
          : [
              interpolateTemplate({
                template: messages.maxLength,
                values: { maxLength },
              }),
            ];
      });
    }
  }

  return (value) =>
    validators.reduce<string[]>((errors, validator) => {
      return errors.concat(validator(value));
    }, []);
}

export type PrepareComponentsDataInput = {
  formDataSource: FormConfig;
  layoutPresets: LayoutPresetsInput;
  screenId: string;
  loadRemoteFeed?: LoadRemoteFeedFn;
  validationMessages?: FormValidationMessages;
};

/**
 * Builds UI components and per-component data sources from form configuration.
 *
 * @param input - Form config, layout presets, screen id, and optional remote loader.
 * @returns UI components and their data source map.
 */
export function prepareComponentsData({
  formDataSource,
  layoutPresets,
  screenId,
  loadRemoteFeed,
  validationMessages = DEFAULT_FORM_VALIDATION_MESSAGES,
}: PrepareComponentsDataInput): ComponentDataResult {
  if (!layoutPresets) {
    throw new Error(
      'prepareComponentsData: layout presets is undefined, please check app has valid preset mapping'
    );
  }

  const formId = `${screenId}_form_component`;
  const uiComponents: FormUiComponent[] = [];
  const uiComponentsDataSource: UIComponentsDataSources = {};

  for (const [index, form] of formDataSource.properties.entries()) {
    const mapping = layoutPresets as Record<string, unknown>;
    const componentPreset = (mapping[form.preset] ??
      (mapping.presets_mappings as Record<string, unknown> | undefined)?.[
        form.preset
      ]) as FormUiComponent | undefined;

    if (!componentPreset) {
      logger.warn(
        `prepareComponentsData: preset '${form.preset}' not found for screen ${screenId}, skipping component`
      );
      continue;
    }

    const component = prepareComponent({
      formData: form,
      qbComponent: componentPreset,
      formId,
      index,
    });

    uiComponents.push(component);
    uiComponentsDataSource[component.id] = {
      dataSource: async () => createDataSource(form, component, loadRemoteFeed),
      validator: createValidator(form, validationMessages),
      component,
      fieldId: form.id,
    };
  }

  return { uiComponents, uiComponentsDataSource };
}
